import React, { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import DateRangePickerLib from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  label,
  placeholder = "Дата и время",
  size = "small",
  variant = "outlined",
  className = "--default-label",
  fullWidth = true,
  sx = {},
  showTimePicker = false,
  single = false,
  signle = false // Поддержка опечатки для обратной совместимости
}) => {
  const isSingle = single || signle;
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    // Пробуем разные форматы для парсинга
    let parsed = moment(dateValue, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601], true);
    if (!parsed.isValid()) {
      parsed = moment(dateValue);
    }
    return parsed.isValid() ? parsed : null;
  };

  const [dateRangeValue, setDateRangeValue] = useState({
    start: parseDate(startDate),
    end: isSingle ? parseDate(startDate) : parseDate(endDate),
  });

  useEffect(() => {
    const newStart = parseDate(startDate);
    const newEnd = isSingle ? parseDate(startDate) : parseDate(endDate);

    setDateRangeValue(prev => {
      // Проверяем, изменились ли значения
      const startChanged =
        (!newStart && prev.start) ||
        (newStart && !prev.start) ||
        (newStart && prev.start && !newStart.isSame(prev.start, 'minute'));

      const endChanged =
        (!newEnd && prev.end) ||
        (newEnd && !prev.end) ||
        (newEnd && prev.end && !newEnd.isSame(prev.end, 'minute'));

      if (startChanged || endChanged) {
        return {
          start: newStart,
          end: newEnd,
        };
      }

      return prev;
    });
  }, [startDate, endDate, isSingle]);

  const handleDateRangeChange = (event, picker) => {
    const start = picker.startDate;
    // В режиме single date picker endDate может быть null, используем startDate
    const end = isSingle ? start : picker.endDate;

    setDateRangeValue({
      start,
      end: end || start,
    });

    if (onChange) {
      let startISO = null;
      let endISO = null;

      if (start) {
        if (showTimePicker) {
          // Используем выбранное время
          startISO = start.toISOString();
        } else {
          // Устанавливаем время начала как 00:00:00
          startISO = start.clone().startOf('day').toISOString();
        }
      }

      if (isSingle) {
        // В режиме одной даты используем ту же дату для end
        endISO = startISO;
      } else if (end) {
        if (showTimePicker) {
          // Используем выбранное время
          endISO = end.toISOString();
        } else {
          // Устанавливаем время окончания как 23:59:59
          endISO = end.clone().endOf('day').toISOString();
        }
      }

      onChange({
        start: startISO,
        end: endISO,
      });
    }
  };

  const getDisplayValue = () => {
    if (isSingle) {
      // В режиме одной даты показываем только одну дату
      if (dateRangeValue.start) {
        const format = showTimePicker ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
        return dateRangeValue.start.format(format);
      }
      return '';
    }
    
    if (dateRangeValue.start && dateRangeValue.end) {
      const format = showTimePicker ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
      return `${dateRangeValue.start.format(format)} - ${dateRangeValue.end.format(format)}`;
    }
    return '';
  };

  // Используем undefined вместо null для initialSettings, если даты не заданы
  const getInitialSettings = () => {
    const currentYear = moment().year();
    const settings = {
      singleDatePicker: isSingle,
      timePicker: showTimePicker,
      showDropdowns: true,
      minYear: currentYear - 10,
      maxYear: currentYear + 10,
      locale: {
        format: showTimePicker ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY',
        separator: ' - ',
        applyLabel: 'Применить',
        cancelLabel: 'Отмена',
        fromLabel: 'От',
        toLabel: 'До',
        customRangeLabel: 'Произвольный',
        weekLabel: 'Н',
        daysOfWeek: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthNames: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        firstDay: 1
      },
      opens: 'left',
    };

    if (showTimePicker) {
      settings.timePicker24Hour = true;
      settings.timePickerIncrement = 1;
    }

    // Добавляем даты только если они валидны
    if (dateRangeValue.start && dateRangeValue.start.isValid()) {
      settings.startDate = dateRangeValue.start;
      // В режиме single date picker endDate должен быть таким же как startDate
      if (isSingle) {
        settings.endDate = dateRangeValue.start;
      }
    }
    if (!isSingle && dateRangeValue.end && dateRangeValue.end.isValid()) {
      settings.endDate = dateRangeValue.end;
    }

    return settings;
  };

  // Создаем ключ для пересоздания компонента при изменении дат
  const pickerKey = `${dateRangeValue.start ? dateRangeValue.start.format('YYYY-MM-DD') : 'null'}_${dateRangeValue.end ? dateRangeValue.end.format('YYYY-MM-DD') : 'null'}`;

  return (
    <DateRangePickerLib
      key={pickerKey}
      initialSettings={getInitialSettings()}
      onApply={handleDateRangeChange}
    >
      <TextField
        value={getDisplayValue()}
        size={size}
        label={label}
        placeholder={placeholder}
        variant={variant}
        className={`${className}`}
        fullWidth={fullWidth}
        InputProps={{readOnly: true,}}
        sx={{...sx}}
      />
    </DateRangePickerLib>
  );
};

export default DateRangePicker;

